// The screen stack (decision 0046: "a screen stack for back and close"). Plain data, no DOM: the UI renders the top
// entry. Each entry gets a unique key, so the same screen pushed twice is two entries (focus memory is per entry).

/** One screen on the stack. */
export interface ScreenEntry {
  id: string;
  params?: unknown;
  /** Unique per push. */
  key: number;
}

/** A change, for listeners and the history. */
export interface StackChange {
  op: "push" | "pop" | "replace" | "reset" | "clear";
  from: string | null;
  to: string | null;
}

/** The stack of screens. */
export class ScreenStack {
  private items: ScreenEntry[] = [];
  private next = 1;
  private listeners: ((c: StackChange) => void)[] = [];
  /** Every change so far (capped at `historyCap`). */
  readonly history: StackChange[] = [];

  constructor(private readonly historyCap = 200) {}

  /** The entries, bottom first (a copy). */
  get entries(): ScreenEntry[] {
    return [...this.items];
  }
  /** The top entry, or null when empty. */
  get top(): ScreenEntry | null {
    return this.items.at(-1) ?? null;
  }
  get size(): number {
    return this.items.length;
  }

  /** Put a screen on top. */
  push(id: string, params?: unknown): ScreenEntry {
    const from = this.top?.id ?? null;
    const e = { id, params, key: this.next++ };
    this.items.push(e);
    this.emit({ op: "push", from, to: id });
    return e;
  }
  /** Take the top screen off; returns it (null when empty). */
  pop(): ScreenEntry | null {
    const e = this.items.pop() ?? null;
    if (e) this.emit({ op: "pop", from: e.id, to: this.top?.id ?? null });
    return e;
  }
  /** Swap the top screen for another (push when empty). */
  replace(id: string, params?: unknown): ScreenEntry {
    const from = this.items.pop()?.id ?? null;
    const e = { id, params, key: this.next++ };
    this.items.push(e);
    this.emit({ op: "replace", from, to: id });
    return e;
  }
  /** Empty the stack and put one screen on it. */
  reset(id: string, params?: unknown): ScreenEntry {
    const from = this.top?.id ?? null;
    this.items = [];
    const e = { id, params, key: this.next++ };
    this.items.push(e);
    this.emit({ op: "reset", from, to: id });
    return e;
  }
  /** Empty the stack. */
  clear(): void {
    if (!this.items.length) return;
    const from = this.top?.id ?? null;
    this.items = [];
    this.emit({ op: "clear", from, to: null });
  }
  /** Is a screen with this id anywhere on the stack? */
  has(id: string): boolean {
    return this.items.some((e) => e.id === id);
  }
  /** Listen to changes; returns the unsubscribe. */
  onChange(fn: (c: StackChange) => void): () => void {
    this.listeners.push(fn);
    return () => void (this.listeners = this.listeners.filter((l) => l !== fn));
  }

  private emit(c: StackChange): void {
    this.history.push(c);
    if (this.history.length > this.historyCap) this.history.shift();
    for (const l of [...this.listeners]) l(c);
  }
}
